import React, { useState, useEffect, useCallback, useLayoutEffect, useRef } from "react";
import "./OnboardingTour.scss";

const PADDING = 8;
const GAP = 14;
const MARGIN = 12; // minimum distance the tooltip keeps from any viewport edge

const OnboardingTour = ({ steps = [], run = false, onFinish, labels = {} }) => {
  const [current, setCurrent] = useState(0);
  const [rect, setRect] = useState(null);
  const [pos, setPos] = useState(null);
  const tooltipRef = useRef(null);

  const total = steps.length;
  const step = steps[current] || null;

  const resolveVisible = useCallback((selector) => {
    if (!selector) return null;
    const el = document.querySelector(selector);
    if (!el) return null;
    const style = window.getComputedStyle(el);
    if (style.display === "none" || style.visibility === "hidden" || style.opacity === "0") {
      return null;
    }
    const r = el.getBoundingClientRect();
    if (r.width <= 0 || r.height <= 0) return null;
    return el;
  }, []);

  const measure = useCallback(
    (doScroll) => {
      if (!step) return false;
      if (!step.selector) {
        setRect(null);
        return true;
      }
      const el = resolveVisible(step.selector) || resolveVisible(step.fallbackSelector);
      if (!el) {
        setRect(null);
        return false;
      }
      if (doScroll) {
        el.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
      }
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      return true;
    },
    [step, resolveVisible]
  );

  // On entering a step: scroll the target into view. If the target doesn't
  // exist yet (e.g. verified-sources/follow-ups appear only after a message),
  // poll briefly until it shows up, then anchor to it.
  useLayoutEffect(() => {
    if (!run) return undefined;
    setPos(null); // hide until repositioned for the new step to avoid stale-position flash
    const found = measure(true);
    if (step && step.selector && !found) {
      const iv = setInterval(() => {
        if (measure(true)) clearInterval(iv);
      }, 400);
      const stop = setTimeout(() => clearInterval(iv), 8000);
      return () => {
        clearInterval(iv);
        clearTimeout(stop);
      };
    }
    return undefined;
  }, [run, current, measure, step]);

  // Compute the tooltip position from the target rect AND the tooltip's own
  // rendered size, clamping to the viewport on every edge so the popover (and
  // its Next/Done buttons) can never end up off-screen. Runs after layout so
  // tooltipRef has real dimensions; re-runs on rect/step changes.
  const reposition = useCallback(() => {
    if (!run || !step) return;
    const tip = tooltipRef.current;
    if (!tip) return;

    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const tw = tip.offsetWidth;
    const th = tip.offsetHeight;

    // Centered fallback when there's no anchored target (e.g. welcome step).
    if (!rect) {
      const top = Math.max(MARGIN, (vh - th) / 2);
      const left = Math.max(MARGIN, (vw - tw) / 2);
      setPos({ top, left });
      return;
    }

    // Horizontal: center over the target, then clamp inside the viewport.
    let left = rect.left + rect.width / 2 - tw / 2;
    left = Math.max(MARGIN, Math.min(left, vw - tw - MARGIN));

    // Vertical: prefer below the target, else above; if it fits neither,
    // clamp into whatever space exists so the buttons stay reachable.
    const belowTop = rect.top + rect.height + PADDING + GAP;
    const aboveTop = rect.top - PADDING - GAP - th;
    let top;
    if (belowTop + th <= vh - MARGIN) {
      top = belowTop;
    } else if (aboveTop >= MARGIN) {
      top = aboveTop;
    } else {
      top = vh - th - MARGIN;
    }
    top = Math.max(MARGIN, Math.min(top, vh - th - MARGIN));

    setPos({ top, left });
  }, [run, step, rect]);

  useLayoutEffect(() => {
    reposition();
  }, [reposition, current]);

  // Keep the highlight + tooltip aligned on resize/scroll without re-scrolling.
  useEffect(() => {
    if (!run) return undefined;
    const onChange = () => {
      measure(false);
      reposition();
    };
    window.addEventListener("resize", onChange);
    window.addEventListener("scroll", onChange, true);
    return () => {
      window.removeEventListener("resize", onChange);
      window.removeEventListener("scroll", onChange, true);
    };
  }, [run, measure, reposition]);

  const finish = useCallback(() => {
    setCurrent(0);
    if (onFinish) onFinish();
  }, [onFinish]);

  useEffect(() => {
    if (!run) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") finish();
      if (e.key === "ArrowRight" && current < total - 1) setCurrent((c) => c + 1);
      if (e.key === "ArrowLeft" && current > 0) setCurrent((c) => c - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [run, current, total, finish]);

  if (!run || !step) return null;

  const isLast = current === total - 1;
  const isFirst = current === 0;

  const vw = window.innerWidth;
  const vh = window.innerHeight;

  let highlightStyle = null;
  if (rect) {
    highlightStyle = {
      top: rect.top - PADDING,
      left: rect.left - PADDING,
      width: rect.width + PADDING * 2,
      height: rect.height + PADDING * 2,
    };
  }

  // Until the first reposition runs, keep the tooltip measurable but hidden to
  // avoid a flash in the wrong spot.
  const tooltipStyle = pos
    ? { top: pos.top, left: pos.left }
    : { top: 0, left: 0, visibility: "hidden" };

  return (
    <div className="onboarding-tour" role="dialog" aria-modal="true">
      {rect ? (
        <>
          <div className="ot-mask" style={{ height: Math.max(0, highlightStyle.top) }} />
          <div
            className="ot-mask"
            style={{
              top: highlightStyle.top + highlightStyle.height,
              height: Math.max(0, vh - (highlightStyle.top + highlightStyle.height)),
            }}
          />
          <div
            className="ot-mask"
            style={{
              top: highlightStyle.top,
              height: highlightStyle.height,
              left: 0,
              width: Math.max(0, highlightStyle.left),
            }}
          />
          <div
            className="ot-mask"
            style={{
              top: highlightStyle.top,
              height: highlightStyle.height,
              left: highlightStyle.left + highlightStyle.width,
              width: Math.max(0, vw - (highlightStyle.left + highlightStyle.width)),
            }}
          />
          <div className="ot-highlight" style={highlightStyle} />
        </>
      ) : (
        <div className="ot-mask ot-mask-full" />
      )}

      <div className="ot-tooltip" style={tooltipStyle} ref={tooltipRef}>
        <div className="ot-tooltip-head">
          <span className="ot-step-count">
            {current + 1} / {total}
          </span>
          <button className="ot-skip" onClick={finish} type="button">
            {labels.skip || "Skip"}
          </button>
        </div>
        <div className="ot-scroll">
          {step.title && <h4 className="ot-title">{step.title}</h4>}
          <p className="ot-body">{step.body}</p>
        </div>
        <div className="ot-dots">
          {steps.map((_, i) => (
            <span
              key={i}
              className={`ot-dot ${i === current ? "active" : ""}`}
              onClick={() => setCurrent(i)}
            />
          ))}
        </div>
        <div className="ot-actions">
          <button
            className="ot-btn ot-btn-ghost"
            onClick={() => setCurrent((c) => Math.max(0, c - 1))}
            disabled={isFirst}
            type="button"
          >
            {labels.back || "Back"}
          </button>
          {isLast ? (
            <button className="ot-btn ot-btn-primary" onClick={finish} type="button">
              {labels.done || "Done"}
            </button>
          ) : (
            <button
              className="ot-btn ot-btn-primary"
              onClick={() => setCurrent((c) => Math.min(total - 1, c + 1))}
              type="button"
            >
              {labels.next || "Next"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default OnboardingTour;
